import React, { useContext } from 'react';
import {
	ProcessTimeline as ProcessTimelineBase,
	type ProcessStep
} from 'components/ui/process-timeline';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { TransferStatus } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { KycStatus } from '../../api/kyc/interfaces/KycStatus';
import { StepState } from '../../types/individualDashboard';

export type TimelineStep = 'requested' | 'kyc' | 'sign' | 'processing' | 'completed';

/**
 * Presencia de este objeto = el traspaso se pidió DESDE la plataforma.
 *
 * Ese cliente no se verifica —lo hizo hace meses, al traerse su primer plan— ni
 * elige plan de destino, así que los dos primeros pasos del flujo de siempre le
 * cuentan algo que no está pasando. Lo que sí tiene pendiente es firmar el
 * boletín (decisión de producto de Marisa, 11/09/26; textos pendientes de que
 * los revise Elena).
 */
export interface BulletinSignature {
	/** Si NO consta firmado se enseña el paso activo, con su botón. */
	signed: boolean;
}

interface TimelineStepDef {
	id: TimelineStep;
	label: string;
	sublabel?: string;
	state: StepState;
	noAction?: boolean;
}

interface ProcessTimelineProps {
	currentStatus: TransferStatus | undefined;
	kycStatus: KycStatus;
	processStarted: string | null;
	planLockedAt?: string | null;
	kycCompletedAt?: string | null;
	executedAt?: string | null;
	activeStepHint?: string;
	isNewPlan?: boolean;
	/** Presente solo en los traspasos pedidos desde la plataforma. */
	bulletinSignature?: BulletinSignature;
	translate: (key: string) => string;
	onStepCtaClick?: (step: TimelineStep) => void;
	/**
	 * Sin botón en el paso de identidad. Lo usa la tarjeta de traspaso de Proa, que nunca abre el
	 * KYC: la identidad va con la persona y la pide la tarjeta de acción de arriba (chirigota#603).
	 */
	hideKycCta?: boolean;
}

function computeTimelineSteps(
	currentStatus: TransferStatus | undefined,
	kycStatus: KycStatus,
	planLockedAt: string | null | undefined,
	translate: (key: string) => string,
	isNewPlan?: boolean,
	bulletinSignature?: BulletinSignature
): TimelineStepDef[] {
	const isPending = currentStatus === 'requested' || currentStatus === 'draft';
	const isInProgress = currentStatus === 'in_progress';
	const isCompleted = currentStatus === 'completed';
	const isKycApproved = kycStatus === 'APPROVED' || kycStatus === 'NOT_REQUIRED';
	const isKycRejected = kycStatus === 'REJECTED';
	const isKycPendingReview = kycStatus === 'PENDING_REVIEW';
	const isKycActive =
		!isKycApproved &&
		(kycStatus === 'PENDING' ||
			kycStatus === 'IN_PROGRESS' ||
			kycStatus === 'NEEDS_INFO' ||
			isKycPendingReview);

	// Step 0: COMPLETED if plan locked (new FF flow) OR transfer already progressed (legacy users
	// who never went through ExplorationView but are already requested/in_progress/completed).
	const step0Done = !!(planLockedAt || isPending || isInProgress || isCompleted);
	const step0: TimelineStepDef = {
		id: 'requested',
		label: translate('individual.process.timeline.requested') || 'Plan de pensiones seleccionado',
		state: step0Done ? StepState.COMPLETED : StepState.ACTIVE,
		noAction: !step0Done
	};

	// Step 1: Verificación de identidad
	let kycState: StepState;
	if (isKycApproved || isInProgress || isCompleted) {
		kycState = StepState.COMPLETED;
	} else if (isKycRejected) {
		kycState = StepState.ERROR;
	} else if (isPending && planLockedAt) {
		kycState = StepState.ACTIVE;
	} else {
		kycState = StepState.PENDING;
	}
	const step1: TimelineStepDef = {
		id: 'kyc',
		label: translate('individual.process.timeline.kyc') || 'Verificación de identidad',
		sublabel: isKycPendingReview
			? translate('individual.process.timeline.kycPendingReviewHint') ||
				'Revisando tu expediente. Puede tardar 24-48 h. Te avisamos por email.'
			: undefined,
		state: kycState,
		noAction: isKycPendingReview
	};

	// Step 2: Tramitando con la gestora
	let processingState: StepState;
	if (isCompleted) {
		processingState = StepState.COMPLETED;
	} else if (isInProgress || (isKycApproved && isPending)) {
		processingState = StepState.ACTIVE;
	} else {
		processingState = StepState.PENDING;
	}
	const step2: TimelineStepDef = {
		id: 'processing',
		label: translate('individual.process.timeline.processing') || 'Tramitando con la gestora',
		sublabel:
			processingState === StepState.ACTIVE
				? translate('individual.process.timeline.processingHint') ||
					'Te avisamos por email cuando avance.'
				: undefined,
		state: processingState,
		noAction: true
	};

	// Step 3: Traspaso realizado / Plan activado
	const step3: TimelineStepDef = {
		id: 'completed',
		label: isNewPlan
			? translate('individual.process.timeline.completedNewPlan') || 'Plan activado'
			: translate('individual.process.timeline.completed') || 'Traspaso realizado',
		state: isCompleted ? StepState.COMPLETED : StepState.PENDING
	};

	if (bulletinSignature) {
		const firmado = bulletinSignature.signed;

		// Ya tiene plan: lo que se cierra aquí es haber pedido el traspaso.
		const step0Plataforma: TimelineStepDef = {
			id: 'requested',
			label: translate('individual.process.timeline.requestedTransfer'),
			state: StepState.COMPLETED
		};

		const stepFirma: TimelineStepDef = {
			id: 'sign',
			label: firmado
				? translate('individual.process.timeline.signed')
				: translate('individual.process.timeline.sign'),
			sublabel: firmado ? undefined : translate('individual.process.timeline.signHint'),
			state: firmado ? StepState.COMPLETED : StepState.ACTIVE,
			noAction: firmado || undefined
		};

		return [step0Plataforma, stepFirma, step2, step3];
	}

	return [step0, step1, step2, step3];
}

const fmtDate = (iso: string, lang: string) =>
	new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'es-ES', {
		day: 'numeric',
		month: 'short'
	});

/**
 * El pintado vive en `ui/process-timeline` (Proa). Aquí se queda lo que depende del backend:
 * `computeTimelineSteps` resuelve en qué estado está cada paso a partir del traspaso y del KYC,
 * y esto solo lo traduce a la forma que espera el componente del sistema.
 *
 * Adoptarlo arregla tres cosas que esta pantalla tenía mal, y ninguna es cosmética:
 *
 * 1. 🔴 ACTIVE y ERROR pintaban el MISMO círculo (`bg-primary` con pulso), así que un KYC
 *    RECHAZADO se veía igual que «en curso, todo bien». Ahora el error es rojo, con
 *    `AlertCircle` y sin pulso — el color nunca es la única señal.
 * 2. La etiqueta del paso completado iba en `text-primary`: sobre la tarjeta da 3,14:1 y no
 *    llega a AA. Pasa a `text-primary-text`, 7,35:1 — el mismo verde, en el tono de primer plano.
 * 3. El botón no llegaba a los 44 px de área táctil. El del sistema los alcanza con un
 *    pseudoelemento, sin agrandar la caja visible.
 */
const ProcessTimeline: React.FC<ProcessTimelineProps> = ({
	currentStatus,
	kycStatus,
	processStarted,
	planLockedAt,
	kycCompletedAt,
	executedAt,
	activeStepHint,
	isNewPlan,
	bulletinSignature,
	translate,
	onStepCtaClick,
	hideKycCta = false
}) => {
	const {
		state: { langCode }
	} = useContext(I18nContext);
	const steps = computeTimelineSteps(
		currentStatus,
		kycStatus,
		planLockedAt,
		translate,
		isNewPlan,
		bulletinSignature
	);

	const stepDate: Partial<Record<TimelineStep, string>> = {
		requested: planLockedAt ?? processStarted ?? undefined,
		kyc: kycCompletedAt ?? undefined,
		processing: executedAt ?? undefined,
		completed: executedAt ?? undefined
	};

	// El sistema decide si pinta botón por si el paso trae `ctaLabel`, no por su estado. Así que
	// la regla de quién puede accionar se resuelve aquí, que es donde se conoce: los pasos activos
	// salvo los marcados `noAction`, y además el paso ya completado, que ofrece la salida al plan.
	const ctaLabel = (step: TimelineStepDef): string | undefined => {
		if (step.id === 'completed' && step.state === StepState.COMPLETED) {
			return translate('individual.process.cta.viewPlan') || 'Empezar a gestionar mi plan →';
		}
		if (step.state !== StepState.ACTIVE || step.noAction) return undefined;
		if (step.id === 'kyc') return hideKycCta ? undefined : translate('individual.process.cta.kyc');
		if (step.id === 'sign') return translate('individual.process.cta.sign');
		return translate('individual.process.cta.confirm');
	};

	// `sublabel` y `activeStepHint` eran dos párrafos separados y el sistema tiene una sola ranura,
	// así que cuando coinciden se juntan en uno. Coinciden de verdad: un KYC en PENDING_REVIEW está
	// activo y ya trae su propio aviso. Se juntan en vez de que uno gane porque ninguno de los dos
	// es descartable — el del paso explica el paso, y el otro, el proceso.
	const sublabel = (step: TimelineStepDef): string | undefined => {
		const hint = step.state === StepState.ACTIVE ? activeStepHint : undefined;
		return [step.sublabel, hint].filter(Boolean).join(' ') || undefined;
	};

	const pasos: ProcessStep[] = steps.map((step) => {
		const fecha = stepDate[step.id];
		return {
			id: step.id,
			label: step.label,
			sublabel: sublabel(step),
			state: step.state,
			date: step.state === StepState.COMPLETED && fecha ? fmtDate(fecha, langCode) : undefined,
			ctaLabel: ctaLabel(step)
		};
	});

	// El sistema no conoce la unión `TimelineStep`, así que entrega un `string`. Se reencuentra el
	// paso en vez de hacer un cast: `step.id` ya viene tipado, y un id que no exista no llama a
	// nadie en lugar de colarse como si fuera válido.
	const alPulsarPaso = onStepCtaClick
		? (id: string) => {
				const paso = steps.find((s) => s.id === id);
				if (paso) onStepCtaClick(paso.id);
			}
		: undefined;

	return (
		<div data-testid="process-timeline">
			<ProcessTimelineBase steps={pasos} onStepCtaClick={alPulsarPaso} testIdPrefix="timeline" />
		</div>
	);
};

export default ProcessTimeline;
export { computeTimelineSteps };
